import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signInWithPhoneNumber, RecaptchaVerifier } from "firebase/auth";
import { auth } from "../firebaseConfig";
import "../App.css";
import { collection, query, where, getDocs, addDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";

function Login() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const recaptchaContainerRef = useRef(null);
  const isSendingRef = useRef(false);
  const verifierRef = useRef(null);

  // Helper function to completely clear reCAPTCHA
  const clearRecaptcha = useCallback(() => {
    if (verifierRef.current) {
      try {
        verifierRef.current.clear();
      } catch (e) {
        console.warn("Error clearing recaptcha:", e);
      }
      verifierRef.current = null;
    }
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        console.warn("Error clearing window recaptcha:", e);
      }
      window.recaptchaVerifier = null;
    }
    const container = document.getElementById("recaptcha-container");
    if (container) {
      container.innerHTML = '';
    }
  }, []);

  // Cleanup recaptcha verifier on unmount
  useEffect(() => {
    return () => {
      clearRecaptcha();
    };
  }, [clearRecaptcha]);

  // Cooldown timer effect
  useEffect(() => {
    let interval = null;
    if (cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((seconds) => {
          if (seconds <= 1) {
            clearInterval(interval);
            return 0;
          }
          return seconds - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldownSeconds]);

  const handleSendCode = async () => {
    if (loading || cooldownSeconds > 0 || isSendingRef.current) {
      return;
    }

    isSendingRef.current = true;
    setLoading(true);
    setErrorMessage("");

    try {
      const trimmedPhoneNumber = phoneNumber.trim();
      if (!trimmedPhoneNumber || trimmedPhoneNumber === "") {
        setErrorMessage("Please enter a phone number.");
        setLoading(false);
        isSendingRef.current = false;
        return;
      }

      let formattedPhoneNumber;
      if (trimmedPhoneNumber.startsWith('0')) {
        formattedPhoneNumber = '+44' + trimmedPhoneNumber.slice(1);
      } else if (trimmedPhoneNumber.startsWith('7')) {
        formattedPhoneNumber = '+44' + trimmedPhoneNumber;
      } else {
        formattedPhoneNumber = trimmedPhoneNumber;
      }

      // Check the firestore invites list for the phone number first
      const invitesRef = collection(db, "invites");
      const q = query(invitesRef, where("phoneNumber", "==", formattedPhoneNumber));

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        const loginFailsRef = collection(db, "loginFails");
        await addDoc(loginFailsRef, {
          phoneNumber: formattedPhoneNumber,
          attemptedAt: new Date()
        });
        setErrorMessage("Sorry, This phone number isn't registered on the guest list. Call Mike or Kerry to get this updated.");
        setLoading(false);
        isSendingRef.current = false;
        return;
      }

      console.log("Phone number found in invites list.");

      // Always clear any existing verifier first
      clearRecaptcha();

      // Ensure the recaptcha container exists
      const recaptchaContainer = document.getElementById("recaptcha-container");
      if (!recaptchaContainer) {
        throw new Error("Recaptcha container not found. Please refresh the page.");
      }

      // Wait for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Create a fresh RecaptchaVerifier for each attempt
      // Using invisible reCAPTCHA which is more reliable for phone auth
      let verifier;
      try {
        verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
          size: "invisible",
          callback: (response) => {
            console.log("reCAPTCHA verified successfully:", response);
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
            clearRecaptcha();
          },
          "error-callback": (error) => {
            console.error("reCAPTCHA error:", error);
            clearRecaptcha();
          }
        });
        verifierRef.current = verifier;
      } catch (verifierError) {
        console.error("Error creating RecaptchaVerifier:", verifierError);
        clearRecaptcha();
        throw new Error("Failed to initialize reCAPTCHA. Please refresh the page and try again.");
      }

      // Render the verifier
      try {
        await verifier.render();
        console.log("reCAPTCHA rendered successfully");
        
        // Wait a bit to ensure verifier is fully ready
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (renderError) {
        console.error("Error rendering recaptcha:", renderError);
        clearRecaptcha();
        throw new Error("Failed to render reCAPTCHA. Please refresh the page and try again.");
      }

      // Verify the verifier is ready
      if (!verifier || !verifierRef.current) {
        clearRecaptcha();
        throw new Error("reCAPTCHA verifier not properly initialized. Please try again.");
      }
      
      // Ensure auth is ready
      if (!auth) {
        throw new Error("Firebase Authentication is not initialized. Please refresh the page.");
      }
      
      // Attempt to send verification code
      console.log("Sending verification code request to Firebase...");
      const result = await signInWithPhoneNumber(auth, formattedPhoneNumber, verifier);
      console.log("Verification code sent successfully");
      
      // Track that a code was sent (for detecting failed logins)
      const loginCodeRequestsRef = collection(db, "loginCodeRequests");
      await addDoc(loginCodeRequestsRef, {
        phoneNumber: formattedPhoneNumber,
        name: querySnapshot.docs[0].data().name || '',
        codeSentAt: new Date(),
        completed: false
      });
      
      setConfirmationResult(result);
      setErrorMessage("");
      
      // Clear verifier after successful send (it's single-use)
      clearRecaptcha();
    } catch (error) {
      console.error("Error sending verification code:", error);
      
      // Always clear verifier on error
      clearRecaptcha();
      
      // Handle specific Firebase errors
      let userFriendlyMessage = "";
      if (error.code === "auth/too-many-requests") {
        userFriendlyMessage = "Too many requests. Please wait a few minutes before trying again.";
        setCooldownSeconds(120);
      } else if (error.code === "auth/invalid-phone-number") {
        userFriendlyMessage = "Invalid phone number format. Please check and try again.";
      } else if (error.code === "auth/quota-exceeded") {
        userFriendlyMessage = "SMS quota exceeded. Please try again later or contact support.";
      } else if (error.code === "auth/captcha-check-failed" || error.code === "auth/invalid-app-credential") {
        userFriendlyMessage = "reCAPTCHA verification failed. This may be due to:\n1) Domain not authorized in Firebase Console (Authentication > Settings > Authorized domains)\n2) Billing not enabled (Blaze plan required for phone auth)\n3) Network or browser issues\n\nPlease refresh the page and try again.";
        setCooldownSeconds(30);
      } else if (error.message) {
        userFriendlyMessage = error.message;
      } else {
        userFriendlyMessage = "An error occurred. Please refresh the page and try again.";
      }
      
      setErrorMessage(userFriendlyMessage);
    } finally {
      setLoading(false);
      isSendingRef.current = false;
    }
  };

  const handleVerifyCode = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      if (!confirmationResult) {
        setErrorMessage("Please request a verification code first.");
        setLoading(false);
        return;
      }

      if (!verificationCode || verificationCode.trim() === "") {
        setErrorMessage("Please enter the verification code.");
        setLoading(false);
        return;
      }

      await confirmationResult.confirm(verificationCode);

      const trimmedPhoneNumber = phoneNumber.trim();
      let formattedPhoneNumber;
      if (trimmedPhoneNumber.startsWith('0')) {
        formattedPhoneNumber = '+44' + trimmedPhoneNumber.slice(1);
      } else if (trimmedPhoneNumber.startsWith('7')) {
        formattedPhoneNumber = '+44' + trimmedPhoneNumber;
      } else {
        formattedPhoneNumber = trimmedPhoneNumber;
      }

      const invitesRef = collection(db, "invites");
      const q = query(invitesRef, where("phoneNumber", "==", formattedPhoneNumber));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const inviteDoc = querySnapshot.docs[0];
        const loginSuccessRef = collection(db, "loginSuccesses");

        await updateDoc(inviteDoc.ref, {
          lastLogin: new Date()
        });

        await addDoc(loginSuccessRef, {
          phoneNumber: inviteDoc.data().phoneNumber,
          name: inviteDoc.data().name || '',
          loggedInAt: new Date()
        });
        
        // Mark any pending code requests as completed
        const loginCodeRequestsRef = collection(db, "loginCodeRequests");
        const codeRequestQuery = query(
          loginCodeRequestsRef,
          where("phoneNumber", "==", formattedPhoneNumber),
          where("completed", "==", false)
        );
        const codeRequestSnapshot = await getDocs(codeRequestQuery);
        const updatePromises = codeRequestSnapshot.docs.map(doc =>
          updateDoc(doc.ref, { completed: true, completedAt: new Date() })
        );
        await Promise.all(updatePromises);
      }

      navigate(from, { replace: true });
    } catch (error) {
      console.error("Error verifying code:", error);
      
      let userFriendlyMessage = "";
      if (error.code === "auth/invalid-verification-code") {
        userFriendlyMessage = "Invalid verification code. Please check and try again.";
      } else if (error.code === "auth/code-expired") {
        userFriendlyMessage = "Verification code has expired. Please request a new code.";
        setConfirmationResult(null);
      } else if (error.message) {
        userFriendlyMessage = error.message;
      } else {
        userFriendlyMessage = "An error occurred verifying the code. Please try again.";
      }
      
      setErrorMessage(userFriendlyMessage);
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <h2 className="login-title">Login with Phone Number</h2>
      <input
        type="text"
        className="login-input"
        placeholder="Enter phone number"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value.trim())}
        disabled={loading || cooldownSeconds > 0}
      />
      <button 
        className="login-button" 
        onClick={handleSendCode}
        disabled={loading || cooldownSeconds > 0}
      >
        {loading ? "Sending..." : cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : "Send Code"}
      </button>

      {errorMessage && (
        <div className="error-message" style={{ 
          color: 'var(--button-danger)', 
          marginTop: '10px', 
          padding: '12px 16px', 
          backgroundColor: 'rgba(200, 90, 90, 0.2)', 
          borderRadius: '8px',
          border: '1px solid rgba(200, 90, 90, 0.3)',
          fontFamily: 'var(--font-body)',
          fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
          maxWidth: '400px',
          width: '100%'
        }}>
          {errorMessage}
        </div>
      )}

      {/* reCAPTCHA container (invisible) */}
      <div 
        id="recaptcha-container" 
        className="recaptcha-container"
        ref={recaptchaContainerRef}
        style={{ 
          marginTop: '10px',
          marginBottom: '10px',
          display: 'none' // Invisible reCAPTCHA doesn't need to be shown
        }}
      ></div>

      {confirmationResult && (
        <>
          <input
            type="text"
            className="login-input"
            placeholder="Enter verification code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            disabled={loading}
            style={{ marginTop: '20px' }}
          />
          <button 
            className="login-button" 
            onClick={handleVerifyCode}
            disabled={loading}
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
        </>
      )}
    </div>
  );
}

export default Login;